import { api } from '@/lib/axios';

export interface Address {
  zipCode: string;
  street: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
  ibgeCode: string;
  ddd: string;
}

export interface Company {
  id: string;
  name: string;
  cnpj?: string;
  mainPhone?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  neighborhood?: string;
  number?: string;
  complement?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCompanyDto {
  name: string;
  cnpj?: string;
  mainPhone?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  neighborhood?: string;
  number?: string;
  complement?: string;
}

export interface UpdateCompanyDto extends Partial<CreateCompanyDto> {}

export interface CompaniesResponse {
  data: Company[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface CompanyEmployee {
  id: string;
  companyId: string;
  clientId: string;
  positionId: string;
  startDate: string;
  endDate?: string;
  active: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  client: {
    id: string;
    name: string;
    email?: string;
    phone?: string;
    document?: string;
  };
  company?: {
    id: string;
    name: string;
    cnpj?: string;
  };
  position: {
    id: string;
    name: string;
    department?: string;
    level?: string;
  };
}

export interface AllEmployeesResponse {
  data: CompanyEmployee[];
  page: number;
  total: number;
  totalPages: number;
}

export interface CreateCompanyEmployeeDto {
  clientId: string;
  positionId: string;
  startDate?: string;
  endDate?: string;
  active?: boolean;
  notes?: string;
  createUser?: boolean;
  userEmail?: string;
  userPassword?: string;
}

export interface UpdateCompanyEmployeeDto {
  positionId?: string;
  startDate?: string;
  endDate?: string;
  active?: boolean;
  notes?: string;
}

export const companiesService = {
  async getAll(params?: {
    search?: string;
    page?: number;
    pageSize?: number;
  }): Promise<CompaniesResponse> {
    const { data } = await api.get('/companies', { params });
    return data;
  },

  async getById(id: string): Promise<Company> {
    const { data } = await api.get(`/companies/${id}`);
    return data;
  },

  async create(company: CreateCompanyDto): Promise<Company> {
    const { data } = await api.post('/companies', company);
    return data;
  },

  async update(id: string, company: UpdateCompanyDto): Promise<Company> {
    const { data } = await api.patch(`/companies/${id}`, company);
    return data;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/companies/${id}`);
  },

  async searchByCNPJ(cnpj: string): Promise<Company | null> {
    const { data } = await api.get(`/companies/search/cnpj/${cnpj}`);
    return data;
  },

  async searchAddress(cep: string): Promise<Address> {
    const { data } = await api.get(`/cep/${cep}`);
    return data;
  },

  // Métodos para gerenciar funcionários
  async getAllEmployees(params?: {
    search?: string;
    page?: number;
    pageSize?: number;
    companyId?: string;
    positionId?: string;
    active?: boolean;
  }): Promise<AllEmployeesResponse> {
    const { data } = await api.get('/companies/employees/all', { params });
    return data;
  },

  async getEmployees(companyId: string): Promise<CompanyEmployee[]> {
    const { data } = await api.get(`/companies/${companyId}/employees`);
    return data;
  },

  async addEmployee(
    companyId: string,
    employee: CreateCompanyEmployeeDto
  ): Promise<CompanyEmployee> {
    const { data } = await api.post(`/companies/${companyId}/employees`, employee);
    return data;
  },

  async updateEmployee(
    companyId: string,
    employeeId: string,
    employee: UpdateCompanyEmployeeDto
  ): Promise<CompanyEmployee> {
    const { data } = await api.patch(
      `/companies/${companyId}/employees/${employeeId}`,
      employee
    );
    return data;
  },

  async removeEmployee(companyId: string, employeeId: string): Promise<void> {
    await api.delete(`/companies/${companyId}/employees/${employeeId}`);
  },
};